/* eslint-disable react/prop-types */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { createColumnHelper } from '@tanstack/react-table';
import CommonInput from 'components/commonInput/CommonInput';
import CommonTable from 'components/commonTable/CommonTable';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoWarning } from 'react-icons/io5';
import { MdEject } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { EjectPowerbankData } from 'reactRedux/thunks/stationThunk/stationThunk';
import { massEjectionValidationSchema } from 'utils/CommonValidation';
import { powerBankStatus } from 'utils/StaticData';

const PowerBankListDataTable = ({ tableData, isLoading, stationId }) => {
  const dispatch = useDispatch();
  const powerbankIdRef = useRef();
  const slotRef = useRef();
  const { onClose } = useDisclosure();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const [isEjectModalOpen, setIsEjectModalOpen] = useState(false);
  const [isMassEjectModalOpen, setIsMassEjectModalOpen] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(massEjectionValidationSchema(tableData?.length || 0)),
  });

  const ejectModalOpen = () => {
    setIsEjectModalOpen(true);
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.powerbankId, {
      id: 'powerbankId',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          width="90px"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Powerbank Id
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor((row) => row?.slot, {
      id: 'slot',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Slot
        </Text>
      ),
      cell: (info) => {
        const slot = info.getValue();
        const slotValue = slot && typeof slot === 'object' ? slot.slot : slot;
        return (
          <Text color={textColor} fontSize="sm" fontWeight="600" align="left">
            {slotValue || 'N/A'}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.batteryLevel, {
      id: 'batteryLevel',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Battery Level(%)
        </Text>
      ),
      cell: (info) => {
        return (
          <Text color={textColor} fontSize="sm" fontWeight="600" align="left">
            {`${info.getValue() || 0} %`}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.status, {
      id: 'status',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Status
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const statusText = powerBankStatus[status] || 'N/A';
        return (
          <Text color={textColor} fontSize="sm" fontWeight="600" align="left">
            {statusText}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.actions, {
      id: 'actions',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          ACTION
        </Text>
      ),
      cell: (row) => (
        <Flex gap={'20px'}>
          <div className="btn btn-primary rounded-3 p-1">
            <Button
              bg="red"
              color="white"
              _hover={{ bg: 'red', opacity: 0.8 }}
              height={'revert'}
              fontSize={'sm'}
              paddingTop={'2px'}
              paddingBottom={'2px'}
              onClick={() => {
                ejectModalOpen();
                powerbankIdRef.current = row?.row?.original?.powerbankId;
                slotRef.current = row?.row?.original?.slot;
              }}
            >
              Eject
            </Button>
          </div>
        </Flex>
      ),
    }),
  ];

  const onConfirm = () => {
    dispatch(
      EjectPowerbankData(
        { powerbankId: powerbankIdRef.current, stationId: stationId },
        () => {},
      ),
    );
    setIsEjectModalOpen(false);
  };

  const handleMassEjection = () => {
    setIsMassEjectModalOpen(true);
  };

  const onMassEjectSubmit = (data) => {
    const payload = {
      stationId: stationId,
      count: data?.count,
    };
    dispatch(EjectPowerbankData(payload, () => {}));
    setIsMassEjectModalOpen(false);
    reset();
  };

  useEffect(() => {
    resetField('count');
  }, [resetField]);

  return (
    <>
      <Box>
        <Flex justifyContent="flex-end" mb={4}>
          <Button
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            leftIcon={<MdEject size={25} />}
            onClick={() => {
              handleMassEjection();
            }}
          >
            Mass Ejection
          </Button>
        </Flex>
        <CommonTable
          columns={columns}
          data={tableData}
          isLoading={isLoading}
          keyValue="id"
        />

        {isEjectModalOpen && (
          <>
            <AlertDialog isOpen={isEjectModalOpen} onClose={onClose} isCentered>
              <AlertDialogOverlay>
                <AlertDialogContent
                  maxWidth="400px"
                  borderRadius="lg"
                  boxShadow="lg"
                  p={4}
                >
                  <AlertDialogBody
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={6}
                  >
                    <Box as={IoWarning} size={80} color={'yellow.500'} mb={4} />
                    <Text
                      fontSize="18px"
                      fontWeight="medium"
                      textAlign="center"
                      mt={5}
                    >
                      {`Are you sure you want to eject a powerbank from slots ${slotRef.current} ?`}
                    </Text>
                  </AlertDialogBody>
                  <AlertDialogFooter justifyContent="center">
                    <Button
                      variant="outline"
                      onClick={() => setIsEjectModalOpen(false)}
                      padding="10px 20px"
                      mr={4}
                    >
                      Cancel
                    </Button>
                    <Button
                      bg="red"
                      color="white"
                      _hover={{ bg: 'red', opacity: 0.8 }}
                      onClick={() => onConfirm()}
                      padding="10px 20px"
                    >
                      Eject
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>
          </>
        )}

        {isMassEjectModalOpen && (
          <AlertDialog
            isOpen={isMassEjectModalOpen}
            onClose={() => {
              setIsMassEjectModalOpen(false);
              resetField('count');
            }}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent
                maxWidth="400px"
                borderRadius="lg"
                boxShadow="lg"
                p={4}
              >
                <form onSubmit={handleSubmit(onMassEjectSubmit)} noValidate>
                  <AlertDialogBody py={6}>
                    <Box display="flex" flexDirection="column" gap={4}>
                      <Text
                        fontSize="22px"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        Mass Ejection
                      </Text>

                      <Flex flexDirection="column" gap={2}>
                        <Flex align="baseline" gap={2}>
                          <Text
                            fontSize="md"
                            fontWeight="500"
                            color="gray.800"
                            marginTop={'9px'}
                          >
                            Number
                          </Text>
                          <Flex align="baseline" flex="1" gap={3}>
                            <Box flex="1">
                              <CommonInput
                                name="count"
                                type="number"
                                placeholder="Enter number"
                                register={register('count')}
                                errors={errors.count}
                                brandStars="red.500"
                                textColor="gray.700"
                                isValidate={false}
                              />
                            </Box>

                            <Text
                              fontSize="xl"
                              fontWeight="500"
                              color="gray.800"
                              textAlign="right"
                              marginTop={'9px'}
                            >
                              / {tableData?.length || 0}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  </AlertDialogBody>

                  <AlertDialogFooter justifyContent="center">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsMassEjectModalOpen(false);
                        resetField('count');
                      }}
                      padding="10px 20px"
                      mr={4}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      bg="red"
                      color="white"
                      _hover={{ bg: 'red', opacity: 0.8 }}
                      padding="10px 20px"
                    >
                      Eject
                    </Button>
                  </AlertDialogFooter>
                </form>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )}
      </Box>
    </>
  );
};

export default PowerBankListDataTable;
